import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Logo from "@/components/Logo";
import LangSwitcher from "@/components/LangSwitcher";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const ROLES = [
    { id: "applicant", label: t("register_role_applicant") },
    { id: "event_user", label: t("register_role_event") },
    { id: "journalist", label: t("register_role_journalist") },
  ];
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", company: "", role: "applicant" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await register(form);
      toast.success(t("login_welcome_toast"));
      if (u.role === "journalist") nav("/press");
      else nav("/portal");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-50 via-white to-wine-50/40">
      <div className="absolute right-6 top-6 z-10"><LangSwitcher /></div>
      <div className="mx-auto grid min-h-screen max-w-7xl items-center px-6 py-10 lg:grid-cols-2 lg:gap-16">
        <div className="hidden lg:block">
          <Logo />
          <div className="mt-12">
            <h1 className="text-5xl font-bold leading-tight text-navy">
              {t("register_brand_title_pre")} <span className="text-wine-600">WineTech</span>
            </h1>
            <p className="mt-5 max-w-md text-lg text-neutral-600">{t("register_brand_subtitle")}</p>
          </div>
        </div>
        <div>
          <div className="mb-6 lg:hidden"><Logo /></div>
          <div className="rounded-3xl border border-wine-100 bg-white p-8 shadow-sm md:p-10" data-testid="register-card">
            <h2 className="text-3xl font-bold text-navy">{t("register_title")}</h2>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("register_first_name")} *</label>
                  <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="register-first-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("register_last_name")} *</label>
                  <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="register-last-name" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("email")} *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="register-email" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("password")} *</label>
                <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="register-password" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("register_company_or_media")}</label>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="register-company" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("register_i_am")} *</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm" data-testid="register-role">
                  {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              {error && <div className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="register-error">{error}</div>}
              <button disabled={loading} type="submit" className="w-full rounded-full bg-wine-500 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 disabled:opacity-50" data-testid="register-submit">
                {loading ? t("register_loading") : t("register_submit")}
              </button>
            </form>
            <div className="mt-5 text-center text-sm text-neutral-600">
              {t("register_have_account")} <Link to="/login" className="font-semibold text-wine-600 hover:text-wine-700" data-testid="register-to-login">{t("login")}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
